import React, { useEffect, useState, useMemo } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { CONTAINER_CONTENT_CLASS } from '../signatures/Signatures';
import usePageTitle from '../../hooks/usePageTitle';
import { useAuth } from '../../AuthContext';
import { useAxios } from '../../core/_axios';
import useEmailCampaignWebSocket from '../../hooks/useEmailCampaignWebSocket';
import useLocalizedCopy from '../../hooks/useLocalizedCopy';
import { triggerEmailFollowUpChanged } from '../../hooks/useEmailFollowUpEnabled';
import { usePageHeader } from '../../contexts/PageHeaderContext';

interface TrainingProgress {
  completed_count: number;
  required_count: number;
  is_complete: boolean;
  remaining: number;
}

const EmailCampaigns: React.FC = () => {
  const { copy } = useLocalizedCopy();
  usePageTitle(copy.FollowUpMessages.title);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const axios = useAxios();
  const { currentUserAccountUniqueId, currentUser, viewingAllAccounts } = useAuth();
  const [trainingProgress, setTrainingProgress] = useState<TrainingProgress | null>(null);
  const [emailFollowUpEnabled, setEmailFollowUpEnabled] = useState<boolean | null>(null);
  const [enablingEmailFollowUp, setEnablingEmailFollowUp] = useState<boolean>(false);
  const [suggestedEmailAddress, setSuggestedEmailAddress] = useState<string>('');
  const [emailAddress, setEmailAddress] = useState<string>('');
  const { setPageHeader } = usePageHeader();

  // Set up WebSocket channel for real-time training progress updates
  const { isConnected } = useEmailCampaignWebSocket({
    onTrainingProgressUpdated: (updatedProgress) => {
      console.log('[EmailCampaigns] Received training progress update:', updatedProgress);
      console.log('[EmailCampaigns] Current training progress:', trainingProgress);
      console.log('[EmailCampaigns] New training progress:', updatedProgress);
      setTrainingProgress(updatedProgress);
    }
  });
  
  console.log('[EmailCampaigns] WebSocket connected:', isConnected, 'Training progress:', trainingProgress);
  
  // Expose refresh function for child components
  const refreshTrainingProgress = () => {
    console.log('[EmailCampaigns] Manual refresh of training progress requested');
    loadTrainingProgress();
  };

  // Memoize navigation items to prevent recreating on every render
  const navigationItems = useMemo(() => (
    <div className="flex gap-4 text-sm">
      <Link
        to="/follow-up-messages/review"
        className={pathname.endsWith('/review') ? "text-primary font-semibold" : "text-gray-600 hover:text-primary"}
      >
        {copy.FollowUpMessages.tabs.reviewQueue}
      </Link>
      <Link
        to="/follow-up-messages/manual-draft"
        className={pathname.endsWith('/manual-draft') ? "text-primary font-semibold" : "text-gray-600 hover:text-primary"}
      >
        {copy.FollowUpMessages.tabs.createMessage}
      </Link>
      <Link
        to="/follow-up-messages/history"
        className={pathname.endsWith('/history') ? "text-primary font-semibold" : "text-gray-600 hover:text-primary"}
      >
        {copy.FollowUpMessages.tabs.history}
      </Link>
      <Link
        to="/follow-up-messages/analytics"
        className={pathname.endsWith('/analytics') ? "text-primary font-semibold" : "text-gray-600 hover:text-primary"}
      >
        {copy.FollowUpMessages.tabs.analytics}
      </Link>
    </div>
  ), [pathname, copy.FollowUpMessages.tabs]);

  // Set up page header with navigation tabs
  useEffect(() => {
    setPageHeader(copy.FollowUpMessages.title, emailFollowUpEnabled ? navigationItems : null);
    
    return () => setPageHeader(null);
  }, [copy.FollowUpMessages.title, navigationItems, emailFollowUpEnabled, setPageHeader]);

  useEffect(() => {
    loadTrainingProgress();
    checkEmailFollowUpEnabled();
  }, [currentUserAccountUniqueId]);

  useEffect(() => {
    // Load suggested email when component mounts if email follow-up is not enabled
    if (emailFollowUpEnabled === false) {
      loadSuggestedEmail();
    }
  }, [emailFollowUpEnabled]);

  const loadTrainingProgress = async () => {
    // Skip training progress when viewing all accounts
    if (!currentUserAccountUniqueId || viewingAllAccounts) return;
    
    try {
      const response = await axios.get(`/authenticated/current-account/email-campaigns/training-progress`, {
        params: {
          account_id: currentUserAccountUniqueId
        }
      });
      
      if (response.data.success) {
        setTrainingProgress(response.data.data.training_progress);
      }
    } catch (err) {
      console.error('Failed to load training progress:', err);
      // Don't show error for training progress, just silently fail
    }
  };

  const checkEmailFollowUpEnabled = async () => {
    // When viewing all accounts, treat as enabled to show campaigns
    if (viewingAllAccounts) {
      setEmailFollowUpEnabled(true);
      return;
    }
    
    if (!currentUserAccountUniqueId) {
      setEmailFollowUpEnabled(false);
      return;
    }
    
    try {
      const response = await axios.get(`/authenticated/current-account/get-current-account`);
      
      if (response.data.success) {
        const account = response.data.data.account;
        const enabled = account.public_data?.email_follow_up_enabled === true;
        setEmailFollowUpEnabled(enabled);
      } else {
        setEmailFollowUpEnabled(false);
      }
    } catch (err) {
      console.error('Failed to check email follow-up status:', err);
      setEmailFollowUpEnabled(false);
    }
  };

  const loadSuggestedEmail = async () => {
    if (!currentUserAccountUniqueId) return;
    
    try {
      const response = await axios.get(`/authenticated/current-account/suggest-email-address`);
      
      if (response.data.success) {
        setSuggestedEmailAddress(response.data.data.suggested_email);
        setEmailAddress(response.data.data.suggested_email);
      }
    } catch (err) {
      console.error('Failed to get suggested email address:', err);
    }
  };

  const confirmAndEnableEmailFollowUp = async () => {
    if (!currentUserAccountUniqueId || !emailAddress || enablingEmailFollowUp) return;
    
    setEnablingEmailFollowUp(true);
    
    try {
      // First get the current account to get its name
      const accountResponse = await axios.get(`/authenticated/current-account/get-current-account`);
      
      let currentAccount;
      
      if (accountResponse.data.success && accountResponse.data.data?.account) {
        currentAccount = accountResponse.data.data.account;
      } else if (accountResponse.data.account) {
        currentAccount = accountResponse.data.account;
      } else if (accountResponse.data.data) {
        currentAccount = accountResponse.data.data;
        if (currentAccount.account) {
          currentAccount = currentAccount.account;
        }
      } else {
        console.error('[EmailCampaigns] Failed to find account in response');
        setEnablingEmailFollowUp(false);
        return;
      }
      
      // Now update with email address and enable email follow-up
      const updatePayload = {
        account_name: currentAccount.name,
        public_data_email_follow_up_enabled: true,
        email_address: emailAddress
      };
      
      const response = await axios.post(`/authenticated/current-account/update-current-account`, updatePayload);
      
      setEmailFollowUpEnabled(true);
      loadTrainingProgress();
      
      // Trigger event to update other components
      triggerEmailFollowUpChanged(true);
      
      // Small delay to ensure event propagates before navigation
      setTimeout(() => {
        // Redirect to review queue after successfully enabling email campaigns
        console.log('[EmailCampaigns] Email campaigns enabled, redirecting to review queue');
        navigate('/follow-up-messages/review');
      }, 100);
    } catch (err) {
      console.error('Failed to enable email follow-up:', err);
      alert('Failed to enable email follow-up. Please try again.');
    } finally {
      setEnablingEmailFollowUp(false);
    }
  };

  // Show loading state while checking email follow-up status
  if (emailFollowUpEnabled === null) {
    return (
      <div className="container-fixed">
        <div className={CONTAINER_CONTENT_CLASS}>
          <div className="flex flex-col justify-center gap-2">
            <h1 className="text-xl font-semibold leading-none text-gray-900">
              {copy.FollowUpMessages.title}
            </h1>
            <div className="flex items-center gap-2 text-sm font-medium text-gray-600">
              {copy.Common.labels.loading}...
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show opt-in message if email follow-up is not enabled (but only for specific accounts, not when viewing all)
  if (!emailFollowUpEnabled && !viewingAllAccounts) {
    return (
      <div className="container-fixed">
          <div className="card">
            <div className="card-body text-center py-12">
              <i className="ki-filled ki-sms text-5xl text-gray-400 mb-4"></i>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {copy.FollowUpMessages.enable.title}
              </h3>
              <p className="text-gray-600 mb-4">
                {copy.FollowUpMessages.enable.description}
              </p>
              <div className="text-sm text-gray-500 mb-6">
                {copy.FollowUpMessages.enable.helpText}
              </div>
              
              {/* Email Address Input */}
              <div className="max-w-md mx-auto mb-6">
                <label htmlFor="emailAddress" className="block text-sm font-medium text-gray-700 mb-2">
                  {copy.FollowUpMessages.enable.emailLabel}
                </label>
                <div className="flex">
                  <input
                    type="text"
                    id="emailAddress"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-l-md focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder={suggestedEmailAddress || "Loading..."}
                    value={emailAddress}
                    onChange={(e) => {
                      const value = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '');
                      setEmailAddress(value);
                    }}
                    maxLength={40}
                  />
                  <span className="inline-flex items-center px-3 border border-l-0 border-gray-300 bg-gray-50 rounded-r-md text-gray-500 text-sm">
                    @{window.location.hostname.includes('localhost') || window.location.hostname.includes('ngrok') ? 'dev.mail' : 'mail'}.signatureguestbook.com
                  </span>
                </div>
                <p className="mt-2 text-xs text-gray-500">
                  {copy.FollowUpMessages.enable.emailDescription}
                </p>
              </div>
              
              <button
                onClick={confirmAndEnableEmailFollowUp}
                disabled={enablingEmailFollowUp || !emailAddress}
                className="btn btn-primary"
              >
                {enablingEmailFollowUp ? (
                  <>
                    <i className="ki-filled ki-loading mr-2"></i>
                    {copy.FollowUpMessages.enable.enabling}
                  </>
                ) : (
                  <>
                    <i className="ki-filled ki-check mr-2"></i>
                    {copy.FollowUpMessages.enable.enableButton}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
    );
  }

  return (
    <div className="container-fixed">
      {trainingProgress && !trainingProgress.is_complete && (
        <div className="mb-5">
          <div className="bg-blue-100 border border-blue-400 text-blue-700 px-4 py-3 rounded relative">
            <div className="flex items-center">
              <i className="ki-filled ki-information-5 text-lg mr-2"></i>
              <div>
                <strong>{copy.FollowUpMessages.training.modeBanner}</strong> {trainingProgress.remaining} {trainingProgress.remaining === 1 ? copy.FollowUpMessages.training.messageRemaining : copy.FollowUpMessages.training.messagesRemaining}
                <div className="text-sm mt-1">
                  {copy.FollowUpMessages.training.progress} {trainingProgress.completed_count}/{trainingProgress.required_count} messages sent
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      <Outlet context={{ trainingProgress, refreshTrainingProgress, emailFollowUpEnabled }} />
    </div>
  );
};

export default EmailCampaigns;